import { memo, useCallback } from 'react'
import { LazyMount } from '@/components/ui'
import { AssetCard } from './asset-card'
import { AssetCardSkeleton } from './asset-card-skeleton'
import type { Asset } from '@/types'

interface AssetGridProps {
  assets: Asset[]
  onPlay: (asset: Asset) => void
  onToggleFavorite: (id: string) => void
  onCopyPrompt: (prompt: string) => void
  onDelete?: (id: string) => void
  /** Set of asset IDs that should animate in as new */
  newAssetIds?: Set<string>
}

/**
 * Memoized asset grid. Each card is wrapped in `LazyMount` so the
 * heavyweight bits (video metadata fetch, animated overlays, modals,
 * download/copy state) only spin up for cards near the viewport.
 * Off-screen cards stay as cheap CSS skeletons until you scroll to
 * them — keeps initial paint snappy and avoids 50× video metadata
 * requests fighting for connection slots.
 */
export const AssetGrid = memo(function AssetGrid({ assets, onPlay, onToggleFavorite, onCopyPrompt, onDelete, newAssetIds }: AssetGridProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {assets.map((asset) => (
        <LazyMount key={asset.id} placeholder={<AssetCardSkeleton />}>
          <AssetGridItem
            asset={asset}
            onPlay={onPlay}
            onToggleFavorite={onToggleFavorite}
            onCopyPrompt={onCopyPrompt}
            onDelete={onDelete}
            isNew={newAssetIds?.has(asset.id)}
          />
        </LazyMount>
      ))}
    </div>
  )
})

interface AssetGridItemProps {
  asset: Asset
  onPlay: (asset: Asset) => void
  onToggleFavorite: (id: string) => void
  onCopyPrompt: (prompt: string) => void
  onDelete?: (id: string) => void
  isNew?: boolean
}

/**
 * Wrapper that memoizes callbacks per-item to prevent unnecessary re-renders.
 */
const AssetGridItem = memo(function AssetGridItem({ asset, onPlay, onToggleFavorite, onCopyPrompt, onDelete, isNew }: AssetGridItemProps) {
  const handlePlay = useCallback(() => onPlay(asset), [onPlay, asset])
  const handleToggleFavorite = useCallback(() => onToggleFavorite(asset.id), [onToggleFavorite, asset.id])
  const handleCopyPrompt = useCallback(() => {
    if (asset.prompt) onCopyPrompt(asset.prompt)
  }, [onCopyPrompt, asset.prompt])
  const handleDelete = useCallback(() => {
    if (onDelete) onDelete(asset.id)
  }, [onDelete, asset.id])

  return (
    <AssetCard
      asset={asset}
      onPlay={handlePlay}
      onToggleFavorite={handleToggleFavorite}
      onCopyPrompt={asset.prompt ? handleCopyPrompt : undefined}
      onDelete={onDelete ? handleDelete : undefined}
      isNew={isNew}
    />
  )
})
